import { useEffect, useState } from "react";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
import { useDownload } from "@/utils/hooks/useDownload.ts";
import { blobToBase64 } from "@/utils/string.ts";
import { useDownloadFileQuery } from "@/data/file.ts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CorrectionQResults, ExerciseDONE, ExerciseResult } from "@/types";
import cn from "classnames";
import Spinner from "@edusogno/primitives/spinner";
import PDFViewer from "@/components/ui/PDFViewer.tsx";
import AnswerSlot from "../exercises/AnswerSlot.tsx";
import downloadIcon from "@/assets/svg/downloadIcon.svg";
import ImgViewer from "@/components/ui/ImgViewer.tsx";

interface AnswerSheetProps {
  exercise?: ExerciseResult | ExerciseDONE;
  renderSlots?: (
    exercise:
      | [string, { question_type: string; correct_answer: string }][]
      | undefined,
  ) => React.ReactNode;
  id?: number;
}

function CorrectionAnswerSheet({
  id,
  exercise,
  renderSlots,
}: AnswerSheetProps) {
  const { download } = useDownload();

  const [pdf, setPdf] = useState<string | null>(null);
  const [showPDF, setShowPDF] = useState<boolean>(true);

  const { isLoading, blob } = useDownloadFileQuery(
    { id: (id as number) ?? exercise?.ex_id },
    showPDF,
  );

  const images = exercise?.files?.images && exercise.files.images.length === 1 ? exercise.files.images : null;

  useEffect(() => {
    if (!blob) return;
    void (async () => {
      const result = await blobToBase64(blob);
      setPdf(result);
    })();
  }, [blob]);

  return (
    <div className="flex flex-col xl:flex-row gap-4 w-full flex-[1_1_0] min-h-0 h-full">
      <div className="flex w-full h-full">
        <div className="flex flex-col w-full min-h-full gap-4 until-xl:overflow-y-auto xl:flex-row">
          <div
            className={cn(
              " rounded-[10px] flex flex-col xl:flex-row bg-white min-h-0",
              {
                "xl:flex-[1_1_60%] xl:w-[60%]  until-xl:min-h-[30vh] xl:min-h-auto w-full flex-[1_1_100%]":
                  showPDF,
              },
            )}
          >
            <div className="relative bg-[#E6E6E6]/100 xl:w-[120px] flex flex-col justify-center items-center rounded-[10px] h-[90px] xl:h-full">
              <button
                className={cn(
                  " text-[10px] font-semibold text-white rounded-[10px] w-[90px] xl:h-[30px] flex flex-col xl:flex-row items-center justify-center px-1 xl:gap-1",
                  {
                    "bg-[#219653]": !showPDF,
                    "bg-[#C80505]": showPDF,
                  },
                )}
                onClick={() => setShowPDF((r) => !r)}
              >
                {!showPDF ? (
                  <>
                    <p>Show PDF</p>
                    <ChevronRight
                      width={14}
                      height={14}
                      className="w-fit until-xl:rotate-90"
                    />
                  </>
                ) : (
                  <>
                    <ChevronLeft
                      width={14}
                      height={14}
                      className="until-xl:rotate-90"
                    />
                    <p>Close</p>
                  </>
                )}
              </button>

              {showPDF && (
                <button
                  onClick={() => download({ id: exercise?.ex_id as number })}
                  className="absolute until-xl:right-1 xl:bottom-2"
                >
                  <img
                    src={downloadIcon}
                    alt="download icon"
                    className="until-xl:w-3/4"
                  />
                </button>
              )}
            </div>

            {isLoading && showPDF ? (
              <div className="flex items-center justify-center w-full h-full col-span-3">
                <Spinner className="!h-12 !w-12 border-t-light" />
              </div>
            ) : (
              // showPDF && <PDFViewer fileBase64={pdf} />

              showPDF && (
                images ? (
                  <ImgViewer images={images[0]} />
                ) : (
                  <PDFViewer fileBase64={pdf} />
                )
              )

            )}
          </div>

          {renderSlots ? (
            renderSlots(
              exercise?.questions && Object.entries(exercise.questions),
            )
          ) : (
            <ScrollArea className="h-full flex-[1_1_40%] min-h-0">
              <div
                id="form-ex"
                className="grid grid-cols-[repeat(auto-fill, minmax(247px, auto))] gap-[12px] w-full"
              >
                {exercise?.questions &&
                  Object.entries((exercise as ExerciseResult).result).map(
                    ([key, q]) => (
                      <AnswerSlot
                        readonly
                        key={key}
                        number={key}
                        {...(q as unknown as CorrectionQResults)}
                      />
                    ),
                  )}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}

export default CorrectionAnswerSheet;
